import {
  getWorkingHoursDayStatus,
  type CompanyWorkingHours,
  type CompanyWorkingHoursSlot,
  type DayOfWeek,
} from "@shaarp/common";

export const WORKING_HOURS_DAYS: readonly DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/** True when at least one day is known, open or closed. */
export function hasKnownWorkingHours(
  workingHours: CompanyWorkingHours | null | undefined,
): boolean {
  if (!workingHours) return false;
  return WORKING_HOURS_DAYS.some(
    (day) => getWorkingHoursDayStatus(workingHours[day]) !== "unknown",
  );
}

/**
 * The words for one day. An absent day is "not provided", an empty one is
 * "closed", and the all-day slot is "open 24 hours".
 */
export function formatWorkingHoursDay(
  slots: readonly CompanyWorkingHoursSlot[] | undefined,
  t: (key: string) => string,
): string {
  switch (getWorkingHoursDayStatus(slots)) {
    case "unknown":
      return t("company.signals.workingHours.unknown");
    case "closed":
      return t("company.signals.workingHours.closed");
    case "open24h":
      return t("company.signals.workingHours.open24h");
    case "slots":
      return (slots ?? []).map((slot) => `${slot.open} - ${slot.close}`).join(", ");
  }
}

function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Whether the place is open at `minutes` since midnight. `slots` are today's;
 * a slot that closes before it opens runs past midnight, so it covers today
 * from its opening time and the early hours of tomorrow, which come from
 * `yesterdaySlots`.
 */
export function isOpenAt(
  slots: readonly CompanyWorkingHoursSlot[],
  minutes: number,
  yesterdaySlots: readonly CompanyWorkingHoursSlot[] = [],
): boolean {
  const openToday = slots.some((slot) => {
    const open = toMinutes(slot.open);
    const close = toMinutes(slot.close);
    return close > open
      ? minutes >= open && minutes < close
      : minutes >= open;
  });
  if (openToday) return true;
  return yesterdaySlots.some((slot) => {
    const open = toMinutes(slot.open);
    const close = toMinutes(slot.close);
    return close <= open && minutes < close;
  });
}
